import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPinIcon, ShieldCheckIcon } from 'lucide-react';
import { Logo } from '../../components/shell/Logo';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { useNetwork } from '../../contexts/NetworkContext';

/**
 * A public Campus Node page, generated from the campus record. Nothing here
 * exposes a representative's personal contact details or unpublished
 * intelligence.
 */
export function CampusNode() {
  const {
    campusId
  } = useParams();
  const network = useNetwork();
  const campus = network.campuses.find(item => item.id === campusId);
  if (!campus || !campus.published) {
    return <div className="min-h-full w-full bg-canvas px-4 py-16" data-id="element-9047" data-mp="2bw27orxuztkg">
        <div className="mx-auto max-w-xl" data-id="element-9048" data-mp="2fvulpk60urx7">
          <EmptyState title="This campus is not open yet" description="Campuses appear here once a campus lead, areas and reward rules are in place." action={<Link to="/network" data-id="element-9050" data-mp="1ubd4zj1zal7m">
                <Button variant="primary" data-id="element-9051" data-mp="1pdv3ueu7daa6">See the campus network</Button>
              </Link>} data-id="element-9049" data-mp="2fwvvllbax8dc" />
        </div>
      </div>;
  }
  const published = network.intel.filter(report => report.campusId === campus.id && report.status === 'Published');
  const verifiedBusinesses = network.leads.filter(lead => lead.campusId === campus.id && lead.kind === 'Business' && ['Verified', 'Active'].includes(lead.stage));
  const reps = network.reps.filter(rep => rep.campusId === campus.id && rep.status === 'Active');
  const campusLead = network.reps.find(rep => rep.id === campus.campusLeadRepId);
  return <div className="min-h-full w-full bg-canvas" data-id="element-9052" data-mp="30ts3awzo3cln">
      <header className="border-b border-line bg-surface" data-id="element-9053" data-mp="2w2a4aeh7fm3b">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-4 sm:px-6" data-id="element-9054" data-mp="2w0qiy9goykuo">
          <Logo subtitle="Campus Network" data-id="element-9055" data-mp="u62km1n00qqx" />
          <Link to="/network" className="ml-auto text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-9056" data-mp="z3uo7sx0a1ay">
            Become a representative
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6" data-id="element-9057" data-mp="pcghb02q5z7s">
        <Badge tone="ok" icon={<ShieldCheckIcon className="h-3.5 w-3.5" data-id="element-9059" data-mp="bdgttvqy7qd7" />} data-id="element-9058" data-mp="bdqkzmnqc3kh">
          Active Campuzon node
        </Badge>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-9060" data-mp="fgmxqwql95gi">
          {campus.name}
        </h1>
        <p className="mt-2 text-[15px] leading-7 text-ink-muted" data-id="element-9061" data-mp="1hx2lmnbxfus">
          {campus.institution} · {campus.city}, {campus.state} · roughly{' '}
          {campus.studentEstimate.toLocaleString('en-NG')} students
        </p>

        <div className="mt-8 grid gap-4 lg:grid-cols-3" data-id="element-9062" data-mp="20t6fpx0wpmqu">
          <div className="space-y-4 lg:col-span-2" data-id="element-9063" data-mp="20soe0kvh6gbj">
            <Panel title="Accommodation zones" description="Where students actually live around this campus." data-id="element-9064" data-mp="3laz46xfe16mq">
              {campus.areas.length === 0 ? <EmptyState title="Areas not surveyed yet" description="A campus lead maps these first." data-id="element-9065" data-mp="3gdyxqlemm1v3" /> : <ul className="divide-y divide-line" data-id="element-9066" data-mp="3qabtrpxn8rt0">
                  {campus.areas.map(area => <li key={area.id} className="flex flex-wrap items-center gap-2 py-3 first:pt-0" data-id="element-9067" data-mp="7w2j0z8znqy8">
                      <MapPinIcon className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-9068" data-mp="2xhsxsnganni" />
                      <span className="min-w-0 flex-1" data-id="element-9069" data-mp="2zltq1q1z6jk">
                        <span className="block text-[13px] font-medium text-ink" data-id="element-9070" data-mp="ht7anellsemf">{area.name}</span>
                        <span className="block text-[12px] text-ink-muted" data-id="element-9071" data-mp="cwqjebj3xk54">{area.kind}</span>
                      </span>
                      <span className="cz-num text-[12px] text-ink-subtle" data-id="element-9072" data-mp="2jm56ljaofnbl">
                        {area.minutesFromGate} min from the gate
                      </span>
                    </li>)}
                </ul>}
            </Panel>

            <Panel title="Campus intelligence" description="Published observations only. Unreviewed reports are never shown here." data-id="element-9073" data-mp="1anvns9g3pryi">
              {published.length === 0 ? <EmptyState title="Nothing published yet" description="Observations appear once a reviewer has verified them." data-id="element-9074" data-mp="1jp39ls1fl0bp" /> : <ul className="divide-y divide-line" data-id="element-9075" data-mp="ryytw6om91gt">
                  {published.map(report => <li key={report.id} className="py-3 first:pt-0" data-id="element-9076" data-mp="vyr1oh66r3l4">
                      <p className="flex flex-wrap items-center gap-2" data-id="element-9077" data-mp="10rsjoy1ovj8x">
                        <Badge tone="neutral" data-id="element-9078" data-mp="494i7sg19iy5">{report.category}</Badge>
                        <span className="ml-auto text-[12px] text-ink-subtle" data-id="element-9079" data-mp="471rf6723lsd">
                          Observed {report.observedOn}
                          {report.expiresOn ? ` · valid to ${report.expiresOn}` : ''}
                        </span>
                      </p>
                      <p className="mt-1 text-[13px] leading-6 text-ink" data-id="element-9080" data-mp="3rxrk18xosl6x">{report.observation}</p>
                    </li>)}
                </ul>}
            </Panel>

            <Panel title="Verified local businesses" data-id="element-9081" data-mp="3iw1s81zw7k8m">
              {verifiedBusinesses.length === 0 ? <EmptyState title="None verified yet" description="Businesses appear after onboarding and verification, not on submission." data-id="element-9082" data-mp="3dyrve1a6c6ar" /> : <ul className="divide-y divide-line" data-id="element-9083" data-mp="345kqehjcisb7">
                  {verifiedBusinesses.map(lead => <li key={lead.id} className="flex flex-wrap items-center gap-2 py-2.5 first:pt-0" data-id="element-9084" data-mp="2v6zev6cakdjx">
                      <span className="min-w-0 flex-1 text-[13px] text-ink" data-id="element-9085" data-mp="2q7w73e4xft89">{lead.name}</span>
                      <Badge tone="ok" data-id="element-9086" data-mp="2gka4uakeyhza">Verified</Badge>
                    </li>)}
                </ul>}
            </Panel>
          </div>

          <div className="space-y-4" data-id="element-9087" data-mp="2lkoety5ks1zy">
            <Panel title="Housing on Campuzon" data-id="element-9088" data-mp="26mchrqeair89">
              <p className="text-[13px] leading-6 text-ink-muted" data-id="element-9089" data-mp="26nnl5p3tpwyu">
                Search verified and unverified listings around {campus.institution}, with distance from
                the gate and roommate matching.
              </p>
              <Link to="/housing" data-id="element-9090" data-mp="2zd4d6lnj7buj">
                <Button variant="primary" size="sm" className="mt-3" data-id="element-9091" data-mp="34a4iguck4akl">
                  Open housing search
                </Button>
              </Link>
            </Panel>

            <Panel title="Campus team" data-id="element-9092" data-mp="2uk1mqme1wiik">
              <p className="cz-num text-2xl font-semibold tracking-tight text-ink" data-id="element-9093" data-mp="2uiqn8yrn1wx9">{reps.length}</p>
              <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-9094" data-mp="3i8uee6nn447d">
                active representative{reps.length === 1 ? '' : 's'}
              </p>
              {campusLead && <p className="mt-2 text-[13px] text-ink" data-id="element-9095" data-mp="1bkln4l77o6ba">
                  Campus Lead: <span className="font-medium" data-id="element-9096" data-mp="16mahkmny8hyv">{campusLead.name}</span>
                </p>}
              <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-9097" data-mp="1lax3j78307z2">
                Representatives are contacted through Campuzon. Personal phone numbers are never
                published.
              </p>
              <Link to="/network" data-id="element-9098" data-mp="slpuvfewy203">
                <Button variant="secondary" size="sm" className="mt-3" data-id="element-9099" data-mp="sn1783mfci2t">
                  Join this campus team
                </Button>
              </Link>
            </Panel>

            <Panel title="Getting around" data-id="element-9100" data-mp="11o8tfnbo1sgj">
              <p className="text-[13px] leading-6 text-ink-muted" data-id="element-9101" data-mp="11kusmjinhrb7">{campus.transportNote}</p>
            </Panel>
          </div>
        </div>
      </main>

      <footer className="mt-16 border-t border-line bg-surface" data-id="element-9102" data-mp="2d1bdwtgvz25z">
        <div className="mx-auto max-w-5xl px-4 py-6 text-[12px] leading-5 text-ink-muted sm:px-6" data-id="element-9103" data-mp="291175el9s7q2">
          Campuzon Verified reflects an in-person inspection reviewed by Campuzon. It is not a statement
          of legal ownership, and an unverified listing has not been inspected by Campuzon.
        </div>
      </footer>
    </div>;
}